import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { CampaignsOverviewHeader } from "./CampaignsOverviewHeader/CampaignsOverviewHeader";
import { ContentTypesEnum, RolePermissions, RouteNames, SortingDirection } from "enums";
import Restricted from "features/Application/Restricted";
import CampaignsContainer from "./containers/CampaignsContainer";
import { fetchOverviewCampaigns } from "../state/thunks/campaignOverviewThunk";
import { bindAction } from "interfaces";
import { Columns, getColumnOptions } from "./getColumnOptions";
import CampaignsFilterForm from "components/filterForms/CampaignsFilterForm/CampaignsFilterForm";
import { useReloadItemViewListItems } from "hooks/useReloadItemViewListItems";
import {
  resetAppliedFilter,
  resetPagination,
  resetSearch,
  resetShouldSort,
  resetSortingColumnName,
  resetSortingDirection,
  setPagination,
  setSearch,
  setShouldSort,
  setSortingColumnName,
  setSortingDirection,
} from "../state/slices/campaignFiltersSlice";
import {
  campaignPaginationSelector,
  campaignSearchSelector,
  campaignSortingColumnNameSelector,
  campaignSortingDirectionSelector,
  campaignsStateSelector,
} from "../state/selectors";
import SearchInput from "components/searchInput/SearchInput";
import CampaignsNoResults from "views/library/campaigns/Overview/CampaignsNoResults";
import { isEmpty } from "lodash";
import { CreateCampaignButton } from "./CampaignsOverviewHeader/CreateCampaignButton";
import { reset } from "../state/slices/campaignOverviewSlice";
import { useNavigate } from "react-router";
import {
  CampaignDeleteAllSuccess,
  CampaignDuplicateAllSuccess,
  CampaignLockV2Success,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { useRtn } from "hooks/useRtn";
import { deleteCampaignsThunk, duplicateCampaignsThunk, lockCampaign } from "../state/thunks/campaignEntityStateThunk";
import { useItemsSelection } from "hooks/useItemsSelection";
import { type CampaignOverview } from "features/Library/SimulatedPhishing/Campaigns/types/state";
import { createTestCampaignSend } from "../state/thunks/campaignSendThunk";
import { useHasPermissions } from "features/Application/hooks";
import { Observable } from "utils";
import DeleteContentConfirmationModal, {
  type RemoveContentObserverInput,
} from "components/deleteContentConfirmationModal/DeleteContentConfirmationModal";

export type CampaignsOverviewProps = PropsFromRedux;

const rtnEvents = [CampaignDuplicateAllSuccess, CampaignDeleteAllSuccess];

const Campaigns: FC<CampaignsOverviewProps> = (props) => {
  const {
    fetchOverviewCampaigns,
    lockCampaign,
    resetSearchFilters,
    setShouldSort,
    createTestSend,
    duplicateCampaigns,
    deleteCampaigns,
    items,
  } = props;
  const hasManagePermission = useHasPermissions([RolePermissions.CampaignsManage]);
  const navigate = useNavigate();

  const { selectedIds, onSelectionChanged, clearSelection } = useItemsSelection<CampaignOverview>();

  const goToCampaignEdit = useCallback(
    (payload: { id: number }) => navigate(`${payload.id.toString()}/configuration`),
    [navigate],
  );

  useRtn(CampaignLockV2Success, goToCampaignEdit);

  const [setReloadListItems, onSearchChanged] = useReloadItemViewListItems(props.setSearch);

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes(`/${RouteNames.contentSimulatedPhishingCampaigns}`)) resetSearchFilters();
    };
  }, [resetSearchFilters]);

  const onTriggerCampaignDeleteObserver = useMemo(
    () => new Observable<(onRemoveConfirm: () => void, removeContentInput: RemoveContentObserverInput) => void>(),
    [],
  );

  const [deleteDisabled, setDeleteDisabled] = useState<boolean>(false);

  useEffect(() => {
    const anySelectedPurchased = items.filter((x) => selectedIds.includes(x.id)).some((x) => x.isPurchased);
    setDeleteDisabled(anySelectedPurchased);
  }, [items, selectedIds, setDeleteDisabled]);

  const onSearchHandler = useCallback(
    (searchTerm: string) => {
      setShouldSort(searchTerm === "");
      // Allow table header to update sort state
      // before fetching data
      setTimeout(() => {
        onSearchChanged(searchTerm);
      }, 0);
    },
    [onSearchChanged, setShouldSort],
  );

  const getSearchBar = () => (
    <SearchInput placeholder="Search for Campaigns..." onChange={onSearchHandler} defaultValue={props.search} />
  );

  const handleEditClick = useCallback(
    (id: number, isDraft: boolean) => {
      isDraft ? goToCampaignEdit({ id }) : lockCampaign(id);
    },
    [goToCampaignEdit, lockCampaign],
  );

  const handleSendTestClick = useCallback((id: number) => createTestSend(id), [createTestSend]);

  const loadCampaignsList = (
    skip: number = 0,
    top: number = 10,
    sortingColumnName: string = Columns.Added.toLowerCase(),
    sortingDirection: SortingDirection = SortingDirection.Descending,
  ) => {
    let column = sortingColumnName,
      direction: string | undefined = sortingDirection;
    if (!props.shouldSort) {
      column = direction = "";
    }
    fetchOverviewCampaigns({ skip, top, sortBy: column, sortDirection: direction });
    setShouldSort(true);
  };

  const onPaginationChange = (page: number) => props.setPagination(page);
  const onSortingColumnNameChange = (name: string) => props.setSortingColumnName(name);
  const onSortingDirectionChange = (direction: SortingDirection) => props.setSortingDirection(direction);

  const duplicate = useCallback(
    (id: number | number[]) => {
      duplicateCampaigns(Array.isArray(id) ? id : [id]);
    },
    [duplicateCampaigns],
  );

  const handleDuplicateSelected = () => {
    clearSelection();
    duplicate(selectedIds);
  };

  const deleteTdfCampaigns = useCallback(
    (id: number | number[]) => {
      const idsToDelete = Array.isArray(id) ? id : [id];
      onTriggerCampaignDeleteObserver.notify(
        () => {
          deleteCampaigns(idsToDelete);
          clearSelection();
        },
        {
          selectedItemsCount: idsToDelete.length,
        },
      );
    },
    [onTriggerCampaignDeleteObserver, deleteCampaigns, clearSelection],
  );

  const columnOptions = useMemo(
    () =>
      getColumnOptions({
        readonly: !hasManagePermission,
        onEditClick: handleEditClick,
        onDuplicate: duplicate,
        onDelete: deleteTdfCampaigns,
        onSendTestClick: handleSendTestClick,
        isMobo: props.isMobo,
      }),
    [hasManagePermission, handleEditClick, duplicate, deleteTdfCampaigns, handleSendTestClick, props.isMobo],
  );

  return (
    <section className="nested-content">
      <CampaignsOverviewHeader
        onDuplicate={handleDuplicateSelected}
        showActionButtons={selectedIds.length > 0}
        selectedItemsCount={selectedIds.length}
        onDelete={() => deleteTdfCampaigns(selectedIds)}
        deleteDisabled={deleteDisabled}
      />
      <Restricted
        permissions={[RolePermissions.CampaignsManage]}
        renderContent={(hasAnyPermission) => (
          <CampaignsContainer
            hideListGridViewSwitcherButton
            fetchData={loadCampaignsList}
            defaultSortingColumnName={props.shouldSort ? props.sortingColumnName : ""}
            sortingDirection={props.shouldSort ? props.sortingDirection : undefined}
            isSelectDisabled={() => !hasAnyPermission}
            // @ts-ignore
            getFilterForm={() => <CampaignsFilterForm />}
            setReloadListItems={setReloadListItems}
            renderSearch={getSearchBar}
            noResultsContent={
              <CampaignsNoResults
                filtered={props.filtered || !isEmpty(props.search)}
                createButton={<CreateCampaignButton />}
              />
            }
            columnOptions={columnOptions}
            permissions={[RolePermissions.CampaignsManage]}
            setPagination={onPaginationChange}
            pagination={props.pagination}
            setSortingColumnName={onSortingColumnNameChange}
            setSortingDirection={onSortingDirectionChange}
            selectedIds={selectedIds}
            onSelectionChanged={onSelectionChanged}
            listViewRtnEvents={rtnEvents}
          />
        )}
      />
      <DeleteContentConfirmationModal
        contentType={ContentTypesEnum.Campaigns}
        onTriggerRemoveContentObserver={onTriggerCampaignDeleteObserver}
        onClose={clearSelection}
      />
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const campaignsState = campaignsStateSelector(state);
  const overview = campaignsState.overview;
  return {
    search: campaignSearchSelector(state),
    pagination: campaignPaginationSelector(state),
    sortingColumnName: campaignSortingColumnNameSelector(state),
    sortingDirection: campaignSortingDirectionSelector(state),
    filtered: !isEmpty(overview.filters.appliedFilter),
    shouldSort: overview.filters.shouldSort,
    isMobo: state.userProfile.isMobo,
    items: overview.campaigns.items,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchOverviewCampaigns: bindAction(fetchOverviewCampaigns, dispatch),
  duplicateCampaigns: bindAction(duplicateCampaignsThunk, dispatch),
  deleteCampaigns: bindAction(deleteCampaignsThunk, dispatch),
  setSearch: bindAction(setSearch, dispatch),
  setPagination: bindAction(setPagination, dispatch),
  setSortingColumnName: bindAction(setSortingColumnName, dispatch),
  setSortingDirection: bindAction(setSortingDirection, dispatch),
  setShouldSort: bindAction(setShouldSort, dispatch),
  resetSearchFilters: () => {
    dispatch(reset());
    dispatch(resetSearch());
    dispatch(resetAppliedFilter());
    dispatch(resetPagination());
    dispatch(resetSortingColumnName());
    dispatch(resetSortingDirection());
    dispatch(resetShouldSort());
  },
  lockCampaign: bindAction(lockCampaign, dispatch),
  createTestSend: bindAction(createTestCampaignSend, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Campaigns);
export default ConnectedComponent;
